import React, { useCallback, useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { withTranslation, useTranslation } from "react-i18next";
import { usePreCheckInContext } from "./contexts/PreCheckInContext";
import { get, isEmpty } from "lodash";
import FloatInput from "./FloatInput";
import ReservationService from "../../backend/ReservationService";
import { ClipLoaderIcon } from "../../components/LoadingIcon";

const AddMyChild = () => {
  const initialFormState = {
    myChildFirstName: "",
    myChildLastName: "",
  };
  const {
    propertyUid,
    bookingNumber,
    language,
    lastName,
    resetBooking,
    userEmail,
    func: { useFormInput, updateGuests, updateErrorPage, setAccessTokenData },
    data: { presets, accessToken, booking, guests },
    components: { PreCheckInHeader, PreCheckInContainer },
  } = usePreCheckInContext();

  const history = useHistory();
  const { t } = useTranslation();
  const [formData, handleFormChange] = useFormInput(initialFormState);
  const [validateForm, setValidateForm] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);

  const goBack = useCallback(() => {
    history.push(
      `/pre-checkin/${propertyUid}/guest-list-management/${bookingNumber}/${encodeURIComponent(
        lastName
      )}?language=${language}&token=${accessToken}&email=${encodeURIComponent(
        userEmail
      )}`
    );
  }, [
    propertyUid,
    bookingNumber,
    lastName,
    language,
    accessToken,
    history,
    userEmail,
  ]);

  useEffect(() => {
    if (loading) {
      if (submitted && resetBooking) {
        setSubmitted(false);
      } else if (!submitted && !resetBooking && !isEmpty(booking)) {
        setLoading(false);
        goBack();
      }
    }
  }, [loading, resetBooking, booking, submitted, goBack]);

  const addBtn = () => {
    setSubmitted(true);
    const validate =
      !isEmpty(formData.myChildFirstName) && !isEmpty(formData.myChildLastName);
    if (validate) {
      let newChildrenData = {
        first_name: formData.myChildFirstName,
        last_name: formData.myChildLastName,
      };
      if (presets.demoMode) {
        keepGuestDataAndMove(newChildrenData);
      } else {
        requestCreateAdditionalGuest(prepareData(newChildrenData));
      }
    }
    setValidateForm(validate);
  };

  const generateDefaultValues = () => {
    const guestId = Math.floor(Math.random() * (9999 - 1000 + 1)) + 1000;
    return {
      guestId,
      id: "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, function (c) {
        const r = (Math.random() * 16) | 0;
        const v = c === "x" ? r : (r & 0x3) | 0x8;
        return v.toString(16);
      }),
      guest_id: guestId,
      avatar: null,
      account_exist: false,
      license_plate: null,
      pre_checked_in_completed: false,
    };
  };

  const keepGuestDataAndMove = (newChildrenData) => {
    updateMyChild({
      ...newChildrenData,
      ...generateDefaultValues(),
    });
    goBack();
  };

  const updateMyChild = (response) => {
    if (isEmpty(response)) return;

    const index = guests
      .allGuests()
      .findIndex((guest) => guest.currentGuest === true);

    if (index !== -1) {
      let currentGuest = guests.allGuests()[index];
      currentGuest.children.push(response);
      guests.UpdateByNumber(index, currentGuest);
      updateGuests(guests);
    }
  };

  const requestCreateAdditionalGuest = async (newChildrenData) => {
    setLoading(true);
    window.scrollTo(0, 0);
    await ReservationService.createAdditionalGuest(
      accessToken,
      propertyUid,
      newChildrenData
    )
      .then((response) => {
        updateMyChild(get(response.data, "guest_details.children[0]"));
        goBack();
      })
      .catch((error) => {
        if (get(error.response, "status") === 401) {
          setAccessTokenData();
        }
        console.error(error);
        updateErrorPage(true);
      });
  };

  const prepareData = (newChildrenData) => {
    const reservationId = get(booking, "resv_id");
    const data = {
      reservation_id: reservationId,
      property_uid: propertyUid,
      guest_details: {
        first_name: "",
        last_name: "",
        email: "",
        children: [newChildrenData],
      },
      reservation: {
        property_uid: propertyUid,
        reservation_id: reservationId,
      },
    };
    return data;
  };

  return (
    <>
      <PreCheckInContainer containerName="add-my-child">
        <PreCheckInHeader
          title={t("ADD_MY_CHILD")}
          headerLink
        />
        {presets && (
          <>
            <section
              id="content"
              className="relative flex flex-col px-9 w-full flex-1 md:flex-0 pt-6">
              <div className="flex flex-col h-full">
                <div className="flex mb-10 w-full">
                  <div className="font-normal m-auto">
                    <p
                      className="mb-12 type-normal large"
                      style={{ color: presets.titleColor }}>
                      {t("ADD_MY_CHILD_DESCRIPTION")}
                    </p>

                    <div className="flex flex-col">
                      <div className="pt-5 mb-24 relative">
                        <FloatInput
                          id="myChildFirstName"
                          name="myChildFirstName"
                          label={t("FIRSTNAME")}
                          span={`*${t("REQUIRED")}`}
                          classNameLabel="mb-6 type-title small"
                          classNameSpan="text-gray-400 type-regular small pl-2"
                          classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                          onChange={handleFormChange}
                          value={formData.myChildFirstName}
                          labelColor={presets.backgroundColorTopBar}
                          borderColor={presets.backgroundColorTopBar}
                        />
                        {!validateForm &&
                          submitted &&
                          isEmpty(formData.myChildFirstName) && (
                            <p className="text-14 text-red-500 mt-5">
                              {t(
                                "PLEASE_FILL_OUT_REQUIRED",
                                "Please fill all the required fields"
                              )}
                            </p>
                          )}
                      </div>
                    </div>

                    <div className="mb-24 relative">
                      <FloatInput
                        id="myChildLastName"
                        name="myChildLastName"
                        label={t("LAST_NAME")}
                        span={`*${t("REQUIRED")}`}
                        classNameLabel="mb-6 type-title small"
                        classNameSpan="text-gray-400 type-regular small pl-2"
                        classNameInput="capitalize border-b w-full px-4 rounded-none outline-none focus:outline-none type-normal large md:mb-10"
                        onChange={handleFormChange}
                        value={formData.myChildLastName}
                        labelColor={presets.backgroundColorTopBar}
                        borderColor={presets.backgroundColorTopBar}
                      />
                      {!validateForm &&
                        submitted &&
                        isEmpty(formData.myChildLastName) && (
                          <p className="text-14 text-red-500 mt-5">
                            {t(
                              "PLEASE_FILL_OUT_REQUIRED",
                              "Please fill all the required fields"
                            )}
                          </p>
                        )}
                    </div>
                  </div>
                </div>
                <button
                  className={`app-button-ipad type-title medium ${presets.buttonStyleName}`}
                  disabled={loading}
                  style={{
                    backgroundColor: presets.backgroundColorTopBar,
                    color: presets.appHomeTextCallToAction,
                  }}
                  onClick={() => addBtn()}>
                  {t("ADD")}
                </button>
              </div>
            </section>
            <button
              className="app-button-mobile type-title medium "
              style={{
                backgroundColor: presets.backgroundColorTopBar,
                color: presets.appHomeTextCallToAction,
                position: "fixed",
                bottom: 0,
                left: 0,
              }}
              disabled={loading}
              onClick={() => addBtn()}>
              {t("ADD")}
            </button>
            {loading && (
              <div className="fixed inset-0 flex justify-center items-center bg-gray-500 opacity-50 z-50">
                <ClipLoaderIcon
                  loadingState={loading}
                  size={30}
                  color={presets.backgroundColorTopBar}
                />
              </div>
            )}
          </>
        )}
      </PreCheckInContainer>
    </>
  );
};
export default withTranslation()(AddMyChild);
